/**
 * Pure helpers deciding whether order line items need the food catalog to be
 * hydrated. Kept free of React Native imports so they are unit-testable.
 */

const hasHydratedItemObject = (lineItem = {}) => Boolean(lineItem?.item && typeof lineItem.item === 'object');

/**
 * True when at least one line item (or one of its extras) lacks an embedded
 * `item` object and therefore needs the food catalog to be hydrated.
 *
 * Cart lines built on this device already carry the full food object, so the
 * (potentially multi-MB) `foods` AsyncStorage blob usually does not need to be
 * read and JSON.parsed on the order-send tap path at all.
 *
 * @param {Array<object>} items Cart / already-sent line items.
 * @returns {boolean}
 */
export const lineItemsNeedFoodLookup = (items = []) => {
    if (!Array.isArray(items)) {
        return false;
    }

    return items.some((item) => {
        if (!hasHydratedItemObject(item)) {
            return true;
        }

        return Array.isArray(item?.extras) && item.extras.some((extra) => !hasHydratedItemObject(extra));
    });
};

/** Max age of the in-memory food lookup before it is re-read from storage. */
export const FOOD_LOOKUP_TTL_MS = 60 * 1000;

let foodLookupCache = null;

/**
 * Drop the in-memory food lookup. Called whenever the `foods` catalog is
 * re-persisted so the next order send hydrates against the fresh catalog.
 *
 * @returns {void}
 */
export const invalidateFoodLookupCache = () => {
    foodLookupCache = null;
};

/**
 * Return the `{ [foodId]: food }` lookup, reading + parsing the (large) `foods`
 * catalog from storage at most once per FOOD_LOOKUP_TTL_MS instead of on every
 * order send. Concurrent callers share one in-flight read. A failed read is not
 * cached.
 *
 * @param {() => Promise<string|null>} readFoods Reads the raw `foods` JSON.
 * @param {(foods: Array<object>) => object} buildLookup Builds the id → food map.
 * @param {number} [now=Date.now()] Current time (injectable for tests).
 * @returns {Promise<object>} The lookup (empty object when nothing is cached).
 */
export const getCachedFoodLookup = async (readFoods, buildLookup, now = Date.now()) => {
    if (foodLookupCache && now - foodLookupCache.loadedAt < FOOD_LOOKUP_TTL_MS) {
        return foodLookupCache.promise;
    }

    const entry = {
        loadedAt: now,
        promise: Promise.resolve()
            .then(readFoods)
            .then((raw) => buildLookup(raw ? JSON.parse(raw) : [])),
    };
    foodLookupCache = entry;

    try {
        return await entry.promise;
    } catch (error) {
        if (foodLookupCache === entry) {
            foodLookupCache = null;
        }
        throw error;
    }
};
